import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useEffect, useMemo, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Controller, useForm } from "react-hook-form";
import { Check, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { alertsAPI, type DailyAlertSettingsPayload } from "@/services/api";
import { useOutletContext } from "react-router";

type FormValues = {
  enabled: boolean;
  sendTime: string;
  timezone: string;
};

type SettingsOutletContext = {
  currentBrand?: {
    brandId?: string;
    id?: string;
  };
};

const DEFAULT_VALUES: FormValues = {
  enabled: true,
  sendTime: "08:00",
  timezone: "UTC",
};

const TIME_OPTIONS = ["07:00", "08:00", "09:00", "10:00", "18:00"];
const TIMEZONE_OPTIONS = [
  "UTC",
  "Europe/Kyiv",
  "Europe/Warsaw",
  "America/New_York",
  "America/Los_Angeles",
];

const mapSettingsToForm = (
  settings: DailyAlertSettingsPayload,
): FormValues => ({
  enabled: Boolean(settings.enabled),
  sendTime: settings.sendTime || DEFAULT_VALUES.sendTime,
  timezone: settings.timezone || DEFAULT_VALUES.timezone,
});

const NotificationAlertPage = () => {
  const { t } = useLocale();
  const { currentBrand } = useOutletContext<SettingsOutletContext>();
  const currentBrandId = useMemo(
    () => currentBrand?.brandId || currentBrand?.id || "",
    [currentBrand?.brandId, currentBrand?.id],
  );

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const alertsEnabled = watch("enabled");
  const disableInputs = isLoading || isSaving || !currentBrandId;

  useEffect(() => {
    let active = true;
    if (!currentBrandId) {
      reset(DEFAULT_VALUES);
      return () => {
        active = false;
      };
    }

    setIsLoading(true);
    void alertsAPI
      .getDailySettings(currentBrandId)
      .then(({ settings }) => {
        if (!active) return;
        reset(mapSettingsToForm(settings));
      })
      .catch(() => {
        if (!active) return;
        toast.error(t("legacy.failed.to.load.notifications.1mck77p"), {
          icon: (
            <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
              <TriangleAlert className="w-4 h-4 text-black" />
            </div>
          ),
          position: "bottom-right",
        });
      })
      .finally(() => {
        if (!active) return;
        setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [currentBrandId, reset]);

  const onSubmit = async (data: FormValues) => {
    if (!currentBrandId) {
      toast.error(t("legacy.select.a.brand.first.7vu7hp"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
      return;
    }
    setIsSaving(true);
    try {
      const { settings } = await alertsAPI.updateDailySettings({
        brandId: currentBrandId,
        ...data,
        includeVisibilityChanges: true,
        includeCompetitorMentions: true,
        includeActionsRecommendations: true,
        includePromptStatus: true,
      });
      reset(mapSettingsToForm(settings));
      toast.success(t("legacy.the.changes.were.saved.ruhync"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <Check className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    } catch {
      toast.error(t("legacy.failed.to.save.notifications.19gzxfm"), {
        icon: (
          <div className="w-6 h-6 bg-muted rounded-lg flex items-center justify-center">
            <TriangleAlert className="w-4 h-4 text-black" />
          </div>
        ),
        position: "bottom-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full overflow-auto">
      <div className="space-y-4">
        <div className="w-full border rounded-md">
          <div className="grid grid-cols-[1fr_120px] border-b">
            <div className="p-4 text-left text-sm font-medium">
              <LocalizedText id="legacy.notifications.ahx7yz" />
            </div>
            <div className="p-4 text-left text-sm font-medium">
              <LocalizedText id="legacy.email.3brsq1" />
            </div>
          </div>

          <div className="divide-y">
            <div className="grid grid-cols-[1fr_120px]">
              <div className="p-4 align-middle text-sm">
                <div className="font-medium">
                  <LocalizedText id="legacy.weekly.digest.emails.1fubrej" />
                </div>
                <div className="text-muted-foreground">
                  <LocalizedText id="legacy.turn.monday.visibility.summary.emails.on.or.off.1wdnzb4" />
                </div>
              </div>
              <div className="p-4 align-middle text-sm flex justify-center">
                <Controller
                  name="enabled"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="data-[state=checked]:bg-[#FF5E00]"
                      disabled={disableInputs}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="w-full border rounded-md p-4">
          <div className="text-sm font-medium">
            <LocalizedText id="legacy.delivery.schedule.1ioumxo" />
          </div>
          <div className="text-muted-foreground text-sm mt-1">
            <LocalizedText id="legacy.choose.when.your.monday.digest.email.should.be.1n7lhut" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#0A0A0A]">
                <LocalizedText id="legacy.send.time.jk9sh8" />
              </p>
              <Controller
                name="sendTime"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={disableInputs || !alertsEnabled}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={t("legacy.select.time.1okhwd4")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-[#0A0A0A]">
                <LocalizedText id="legacy.timezone.1dm4bta" />
              </p>
              <Controller
                name="timezone"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={disableInputs || !alertsEnabled}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={t("legacy.select.timezone.1hc143q")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </div>

        {!currentBrandId && (
          <div className="text-sm text-muted-foreground text-center">
            <LocalizedText id="legacy.select.a.brand.to.configure.notifications.citfuc" />
          </div>
        )}

        <div className="w-full flex justify-center mt-4">
          <Button
            type="submit"
            className="w-fit hover:bg-[#ff8138] hover:text-white text-white cursor-pointer bg-[#FF5E00]"
            disabled={disableInputs}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default NotificationAlertPage;
